/**
 * DriveZone — AuthGuard Component
 *
 * Wraps all protected routes (/app/*, /subscribe, /account).
 *
 * On mount:
 *   1. Calls GET /api/auth/me/ to verify the session is valid
 *   2. If 200 — populates auth store, renders children
 *   3. If 401 — checks PUBLIC_SEARCH_MODE (temporary promo); if on, renders
 *      anyway with no logged-in user; if off, redirects to /login as normal
 *
 * Shows a loading spinner while the /me/ check is in progress.
 * This prevents a flash of protected content before the check completes.
 */

import { useEffect, useState } from 'react'
import { useNavigate, Outlet } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import api from '@/lib/api'
import type { AuthUser } from '@/stores/authStore'

export default function AuthGuard() {
  const navigate = useNavigate()
  const { isAuthenticated, isInitialised, setUser, setInitialised, logout } =
    useAuthStore()
  // Temporary promo escape hatch — true only while PUBLIC_SEARCH_MODE is on
  // and this visitor isn't logged in. Lets /app/search (etc.) render with no
  // account at all. Remove this state + its checks once the promo ends.
  const [publicModeAllowed, setPublicModeAllowed] = useState(false)

  useEffect(() => {
    // If we already know the auth state, don't check again
    if (isInitialised) return

    const controller = new AbortController()

    api
      .get<AuthUser>('/api/auth/me/', { signal: controller.signal })
      .then((res) => {
        setUser(res.data)
        setInitialised()
      })
      .catch(async (err: unknown) => {
        const axiosCode = (err as { code?: string })?.code
        // Component unmounted while the request was in-flight — abort silently.
        if (axiosCode === 'ERR_CANCELED') return

        // Race-condition guard: if the user logged in while /me/ was in-flight,
        // their store is already populated — just mark initialised and stay put.
        if (useAuthStore.getState().isAuthenticated) {
          setInitialised()
          return
        }

        try {
          const { data } = await api.get<{ public_search_mode: boolean }>(
            '/api/auth/public-config/',
            { signal: controller.signal }
          )
          if (data.public_search_mode) {
            setPublicModeAllowed(true)
            setInitialised()
            return
          }
        } catch (cfgErr: unknown) {
          if ((cfgErr as { code?: string })?.code === 'ERR_CANCELED') return
        }
        logout()
        setInitialised()
        navigate('/login', { replace: true })
      })

    return () => controller.abort()
  }, [isInitialised, navigate, setUser, setInitialised, logout])

  // Still checking
  if (!isInitialised) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      </div>
    )
  }

  // Checked but not authenticated, and the promo isn't on — redirect happened above
  if (!isAuthenticated && !publicModeAllowed) return null

  // Authenticated, or the temporary public-search promo let this visitor through
  return <Outlet />
}
